import type { LabConfig } from '../types';

// WebAssembly module interfaces generated from AssemblyScript sources.

export interface WasmCpuExports extends WebAssembly.Exports {
  memory: WebAssembly.Memory;
  runIntegerAlu(iterations: number): number;
  runFloatAlu(iterations: number): number;
  runSimdInt(iterations: number): number;
  runSimdFloat(iterations: number): number;
  runBranchPredict(iterations: number, predictable: number): number;
  runDivMod(iterations: number): number;
  runBigIntLike(iterations: number): number;
  busyLoop(iterations: number): number;
  buildPointerChasingChain(bufferOffset: number, count: number): void;
  runPointerChasing(bufferOffset: number, iterations: number): number;
  runSequentialBandwidth(bufferOffset: number, bytes: number): number;
  __new(size: number, id: number): number;
  __pin(ptr: number): number;
  __unpin(ptr: number): void;
  __collect(): void;
}

export interface WasmCpuModule {
  exports: WasmCpuExports;
  memory: WebAssembly.Memory;
  runIntegerAlu(iterations: number): number;
  runFloatAlu(iterations: number): number;
  runSimdInt(iterations: number): number;
  runSimdFloat(iterations: number): number;
  runBranchPredict(iterations: number, predictable: number): number;
  runDivMod(iterations: number): number;
  runBigIntLike(iterations: number): number;
  busyLoop(iterations: number): number;
  buildPointerChasingChain(bufferPtr: number, count: number): void;
  runPointerChasing(bufferPtr: number, iterations: number): number;
  runSequentialBandwidth(bufferPtr: number, bytes: number): number;
}

export interface WasmBoundaryExports extends WebAssembly.Exports {
  memory: WebAssembly.Memory;
  wasmToJs(iterations: number): number;
  known0(): number;
  known1(x: number): number;
  known2(x: number, y: number): number;
  callGeneric2(iterations: number): number;
  setGlobalOne(x: number): void;
  setGlobalTwo(x: number, y: number): void;
  getGlobal0(): number;
  getGlobal1(x: number): number;
  ifAddWasm(iterations: number, a: number, b: number): number;
  busyLoop(iterations: number): number;
}

export interface WasmBoundaryModule {
  exports: WasmBoundaryExports;
  memory: WebAssembly.Memory;
  wasmToJs(iterations: number): number;
  known0(): number;
  known1(x: number): number;
  known2(x: number, y: number): number;
  callGeneric2(iterations: number): number;
  setGlobalOne(x: number): void;
  setGlobalTwo(x: number, y: number): void;
  getGlobal0(): number;
  getGlobal1(x: number): number;
  ifAddWasm(iterations: number, a: number, b: number): number;
  busyLoop(iterations: number): number;
}

export interface WasmCacheExports extends WebAssembly.Exports {
  memory: WebAssembly.Memory;
  buildChain(startPtr: number, count: number, stride: number): void;
  buildChainFromIndices(startPtr: number, count: number, stride: number, indicesPtr: number): void;
  iterate(startPtr: number, iterations: bigint): number;
  timedIterate(startPtr: number, iterations: bigint, clockPtr: number): bigint;
  writeU64(ptr: number, value: bigint): void;
  readU64(ptr: number): bigint;
  busyLoop(iterations: number): number;
}

export interface WasmCacheModule {
  exports: WasmCacheExports;
  memory: WebAssembly.Memory;
  buildChain(startPtr: number, count: number, stride: number): void;
  buildChainFromIndices(startPtr: number, count: number, stride: number, indicesPtr: number): void;
  iterate(startPtr: number, iterations: bigint): number;
  timedIterate(startPtr: number, iterations: bigint, clockPtr: number): bigint;
  writeU64(ptr: number, value: bigint): void;
  readU64(ptr: number): bigint;
  busyLoop(iterations: number): number;
}

let cacheModule: WasmCacheModule | null = null;
let boundaryModule: WasmBoundaryModule | null = null;

let cpuModule: WasmCpuModule | null = null;

const AS_ID_I32_ARRAY = 3; // AssemblyScript runtime id for Int32Array
const AS_ID_U8_ARRAY = 4;  // AssemblyScript runtime id for Uint8Array

function wrapTypedArray<T extends Int32Array | Uint8Array>(
  exports: WasmCpuExports,
  arr: T,
  asId: number
): number {
  const byteLength = arr.byteLength;
  const ptr = exports.__new(byteLength, asId);
  exports.__pin(ptr);
  const view = new Uint8Array(exports.memory.buffer, ptr, byteLength);
  view.set(new Uint8Array(arr.buffer, arr.byteOffset, byteLength));
  return ptr;
}

export async function loadWasmCpu(url: string): Promise<WasmCpuModule> {
  if (cpuModule) return cpuModule;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch WASM from ${url}: ${response.status} ${response.statusText}`);
  }

  const { instance } = await WebAssembly.instantiateStreaming(response, {
    env: {
      abort: (messagePtr: number, filePtr: number, line: number, column: number) => {
        throw new Error(`WASM abort at ${filePtr}:${line}:${column}`);
      },
    },
  });

  const exports = instance.exports as WasmCpuExports;

  cpuModule = {
    exports,
    memory: exports.memory,
    runIntegerAlu: exports.runIntegerAlu.bind(null),
    runFloatAlu: exports.runFloatAlu.bind(null),
    runSimdInt: exports.runSimdInt.bind(null),
    runSimdFloat: exports.runSimdFloat.bind(null),
    runBranchPredict: exports.runBranchPredict.bind(null),
    runDivMod: exports.runDivMod.bind(null),
    runBigIntLike: exports.runBigIntLike.bind(null),
    busyLoop: exports.busyLoop.bind(null),
    buildPointerChasingChain: (bufferPtr: number, count: number) => {
      exports.buildPointerChasingChain(bufferPtr, count);
    },
    runPointerChasing: (bufferPtr: number, iterations: number) => {
      return exports.runPointerChasing(bufferPtr, iterations);
    },
    runSequentialBandwidth: (bufferPtr: number, bytes: number) => {
      return exports.runSequentialBandwidth(bufferPtr, bytes);
    },
  };

  return cpuModule;
}

export function getCpuModule(): WasmCpuModule | null {
  return cpuModule;
}

export async function loadWasmBoundary(url: string): Promise<WasmBoundaryModule> {
  if (boundaryModule) return boundaryModule;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch boundary WASM from ${url}: ${response.status} ${response.statusText}`);
  }

  const jsCall = (a: number, b: number): number => a + b;

  const { instance } = await WebAssembly.instantiateStreaming(response, {
    env: {
      abort: (messagePtr: number, filePtr: number, line: number, column: number) => {
        throw new Error(`Boundary WASM abort at ${filePtr}:${line}:${column}`);
      },
      jsCall,
    },
  });

  const exports = instance.exports as WasmBoundaryExports;

  boundaryModule = {
    exports,
    memory: exports.memory,
    wasmToJs: exports.wasmToJs.bind(null),
    known0: exports.known0.bind(null),
    known1: exports.known1.bind(null),
    known2: exports.known2.bind(null),
    callGeneric2: exports.callGeneric2.bind(null),
    setGlobalOne: exports.setGlobalOne.bind(null),
    setGlobalTwo: exports.setGlobalTwo.bind(null),
    getGlobal0: exports.getGlobal0.bind(null),
    getGlobal1: exports.getGlobal1.bind(null),
    ifAddWasm: exports.ifAddWasm.bind(null),
    busyLoop: exports.busyLoop.bind(null),
  };

  return boundaryModule;
}

export function getBoundaryModule(): WasmBoundaryModule | null {
  return boundaryModule;
}

export async function loadWasmCache(url: string): Promise<WasmCacheModule> {
  if (cacheModule) return cacheModule;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch cache WASM from ${url}: ${response.status} ${response.statusText}`);
  }

  const seed = () => Date.now();

  const { instance } = await WebAssembly.instantiateStreaming(response, {
    env: {
      abort: (messagePtr: number, filePtr: number, line: number, column: number) => {
        throw new Error(`Cache WASM abort at ${filePtr}:${line}:${column}`);
      },
      seed,
    },
  });

  const exports = instance.exports as WasmCacheExports;

  cacheModule = {
    exports,
    memory: exports.memory,
    buildChain: exports.buildChain.bind(null),
    buildChainFromIndices: exports.buildChainFromIndices.bind(null),
    iterate: exports.iterate.bind(null),
    timedIterate: exports.timedIterate.bind(null),
    writeU64: exports.writeU64.bind(null),
    readU64: exports.readU64.bind(null),
    busyLoop: exports.busyLoop.bind(null),
  };

  return cacheModule;
}

export function getCacheModule(): WasmCacheModule | null {
  return cacheModule;
}

// Worker-specific loader. Each worker loads its own instance to avoid sharing.
export interface WasmWorkerExports extends WebAssembly.Exports {
  memory: WebAssembly.Memory;
  fixedWork(iterations: number): number;
}

export async function loadWasmWorker(url: string): Promise<WasmWorkerExports> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch worker WASM from ${url}: ${response.status} ${response.statusText}`);
  }
  const { instance } = await WebAssembly.instantiateStreaming(response, {
    env: {
      abort: (messagePtr: number, filePtr: number, line: number, column: number) => {
        throw new Error(`WASM abort at ${filePtr}:${line}:${column}`);
      },
    },
  });
  return instance.exports as WasmWorkerExports;
}
